import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login, { AuthCallback } from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import GlobalCalendar from "@/pages/GlobalCalendar";
import CommunityManagement from "@/pages/CommunityManagement";
import ContentPiloting from "@/pages/ContentPiloting";
import PilotageEspace from "@/pages/PilotageEspace";
import Clients from "@/pages/Clients";
import ClientWorkspace from "@/pages/ClientWorkspace";
import PublicationDetail from "@/pages/PublicationDetail";
import Contenus from "@/pages/Contenus";
import IdeasPage from "@/pages/IdeasPage";
import Tasks from "@/pages/Tasks";
import NotificationsPage from "@/pages/NotificationsPage";
import MediaLibrary from "@/pages/MediaLibrary";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Statistiques from "@/pages/Statistiques";
import RapportView from "@/pages/cm/RapportView";
import Demandes from "@/pages/Demandes";
import Agenda from "@/pages/Agenda";
import Prospects from "@/pages/Prospects";
import Appointments from "@/pages/Appointments";
import RdvDetail from "@/pages/RdvDetail";
import Accounting from "@/pages/Accounting";
import { TimerProvider } from "@/context/TimerContext";
import Review from "@/pages/Review";
import QuotePublic from "@/pages/QuotePublic";
import Graphisme from "@/pages/Graphisme";
import GraphismeDocuments from "@/pages/GraphismeDocuments";
import GraphismeProject from "@/pages/GraphismeProject";
import GraphismeReset from "@/pages/GraphismeReset";
import GraphismeClientFiche from "@/pages/GraphismeClientFiche";
import GraphismeApercu from "@/pages/GraphismeApercu";
import GraphismeEspaceDevis from "@/pages/GraphismeEspaceDevis";
import GraphismeEspace from "@/pages/GraphismeEspace";
import GraphismeEspaceProjet from "@/pages/GraphismeEspaceProjet";
import GraphismeLogin from "@/pages/GraphismeLogin";
import WebEspaceProjet from "@/pages/WebEspaceProjet";
import GraphismeCreation from "@/pages/GraphismeCreation";
import SitesWeb from "@/pages/SitesWeb";
import WebProject from "@/pages/WebProject";
import BookingPage from "@/pages/BookingPage";
import BookingManage from "@/pages/BookingManage";

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <span className="font-heading text-2xl font-bold tracking-tight">DGA</span>
          <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function ClientRouter() {
  const { clientId } = useParams();
  const [info, setInfo] = useState(undefined);
  const [mode, setMode] = useState("cm");
  useEffect(() => {
    setInfo(undefined);
    api.get(`/clients/${clientId}`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [clientId]);
  if (info === undefined) {
    return (
      <div className="py-20 flex justify-center">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }
  if (!info) return <Navigate to="/clients" replace />;
  const svc = info.service_type || "communication";
  if (info.kind === "ponctuel" || svc === "communication") return <ClientWorkspace />;
  if (svc === "graphisme") return <GraphismeClientFiche client={info} />;
  // Client mixte : une seule fiche, deux interfaces métier avec bascule immédiate
  return (
    <div className="space-y-4">
      <div className="inline-flex bg-neutral-100 rounded-full p-1" data-testid="client-space-toggle">
        <button onClick={() => setMode("cm")} data-testid="toggle-cm"
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${mode === "cm" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`}>
          Community Management
        </button>
        <button onClick={() => setMode("graphisme")} data-testid="toggle-graphisme"
          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${mode === "graphisme" ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`}>
          Graphisme
        </button>
      </div>
      {mode === "cm" ? <ClientWorkspace /> : <GraphismeClientFiche client={info} />}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/review/:token" element={<Review />} />
            <Route path="/devis/:slug/:token" element={<QuotePublic />} />
            <Route path="/connexion" element={<GraphismeLogin />} />
            <Route path="/graphisme/login" element={<Navigate to="/connexion" replace />} />
            <Route path="/graphisme/reset/:token" element={<GraphismeReset />} />
            <Route path="/graphisme/espace" element={<GraphismeEspace />} />
            <Route path="/graphisme/espace/pilotage" element={<PilotageEspace />} />
            <Route path="/graphisme/espace/devis/:qid" element={<GraphismeEspaceDevis />} />
            <Route path="/graphisme/espace/projet/:pid" element={<GraphismeEspaceProjet />} />
            <Route path="/graphisme/espace/web/:pid" element={<WebEspaceProjet />} />
            <Route path="/booking/:token" element={<BookingPage />} />
            <Route path="/booking/manage/:token/:manage" element={<BookingManage />} />
            <Route element={<Protected><TimerProvider><Layout /></TimerProvider></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/calendrier" element={<GlobalCalendar />} />
              <Route path="/cm" element={<CommunityManagement />} />
              <Route path="/cm/pilotage" element={<ContentPiloting />} />
              <Route path="/cm/rapport/:rid" element={<RapportView />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:clientId" element={<ClientRouter />} />
              <Route path="/publications/:pubId" element={<PublicationDetail />} />
              <Route path="/contenus" element={<Contenus />} />
              <Route path="/idees" element={<IdeasPage />} />
              <Route path="/taches" element={<Tasks />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/medias" element={<MediaLibrary />} />
              <Route path="/parametres" element={<Settings />} />
              <Route path="/profil" element={<Profile />} />
              <Route path="/statistiques" element={<Statistiques />} />
              <Route path="/demandes" element={<Demandes />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/prospection" element={<Prospects />} />
              <Route path="/rdv" element={<Appointments />} />
              <Route path="/rdv/:id" element={<RdvDetail />} />
              <Route path="/comptabilite" element={<Accounting />} />
              <Route path="/graphisme" element={<Graphisme />} />
              <Route path="/graphisme/apercu/:clientId" element={<GraphismeApercu />} />
                <Route path="/graphisme/documents" element={<GraphismeDocuments />} />
              <Route path="/graphisme/projet/:pid" element={<GraphismeProject />} />
              <Route path="/graphisme/projet/:pid/creation/:cid" element={<GraphismeCreation />} />
              <Route path="/sites-web" element={<SitesWeb />} />
              <Route path="/sites-web/projet/:pid" element={<WebProject />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
